import React, { useState, useEffect } from 'react';
import { X, Phone, Package, CheckCircle, Navigation, Mail, MapPin, Clock } from 'lucide-react';
import { useLeadSubmission } from '../hooks/useLeadSubmission';

interface BlogLeadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  productName?: string;
  image?: string;
  title?: string;
  quantity?: string;
}

const BlogLeadPopup: React.FC<BlogLeadPopupProps> = ({ 
  isOpen, 
  onClose, 
  productName = '', 
  image = '',
  title = '',
  quantity = ''
}) => {
  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [quantityValue, setQuantityValue] = useState(quantity);
  const [quantityUnit, setQuantityUnit] = useState('kg');
  const [location, setLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [error, setError] = useState('');
  const { submitLead } = useLeadSubmission();

  const validateMobile = (number: string) => {
    return /^[6-9]\d{9}$/.test(number);
  };

    const detectLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setIsDetectingLocation(true);
    setError('');

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

      if (permissionStatus.state === 'denied') {
        setError('Location access denied. Please enable it in your browser settings.');
        setIsDetectingLocation(false);
        return;
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const { latitude, longitude } = position.coords;
      
      // Use a reverse geocoding service to get address
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`
      );
      const data = await response.json();
      
      if (data.display_name) {
        setLocation(data.display_name);
      } else {
        setError('Could not determine location name');
      }
    } catch (error) {
      console.error('Error detecting location:', error);
      if (typeof error === 'object' && error !== null && 'code' in error && (error as { code: number }).code === 1) {
        setError('Location access denied. Please enable location services.');
      } else {
        setError('Failed to detect location. Please enter manually.');
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setMobile('');
      setQuantityValue(quantity);
      setQuantityUnit('kg');
      setLocation('');
      setError('');
    }
  }, [isOpen, quantity]);

  const handleMobileSubmit = () => {
    if (validateMobile(mobile)) {
      setStep(2);
    }
  };

  const handleFinalSubmit = async () => {
    if (!quantityValue.trim()) {
      setError('Please enter the quantity you need');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Here you would typically send the data to your backend
      const inquiryData = {
        mobile,
        quantity: `${quantityValue} ${quantityUnit}`,
        location,
        productName: productName || title,
        source: 'blog',
        timestamp: new Date().toISOString()
      };

      console.log('Inquiry submitted:', inquiryData);
      
      // You can add actual API call here
      // await submitInquiry(inquiryData);
      
      setStep(3);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setError('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form after a short delay to allow modal to close
    setTimeout(() => {
      setStep(1);
      setMobile('');
      setQuantityValue(quantity);
      setQuantityUnit('kg');
      setLocation('');
      setError('');
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            Get Quote
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Product Preview */}
        {(image || title) && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 sm:p-6 border-b border-amber-200">
            <div className="flex items-center space-x-4">
              {image && (
                <div className="flex-shrink-0">
                  <img 
                    src={image} 
                    alt={title || 'Product'}
                    className="w-16 h-16 rounded-lg object-cover shadow-md"
                  />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 text-sm sm:text-base">
                  {title || productName || 'Blog Post Inquiry'}
                </h3>
                <p className="text-amber-700 text-xs sm:text-sm mt-1">
                  Get pricing and availability for products mentioned in this article
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh] sm:max-h-[65vh]">
          {step === 1 && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center">
                <Phone className="mx-auto text-amber-600 mb-2 sm:mb-4" size={36} />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Get Quote for This Article
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm">
                  We'll contact you with the best prices and offers for products mentioned in this article
                </p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Mobile Number *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs sm:text-sm">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className={`w-full pl-10 pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs sm:text-base ${
                      mobile && !validateMobile(mobile) ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="9876543210"
                    maxLength={10}
                  />
                </div>
                {mobile && !validateMobile(mobile) && (
                  <p className="text-red-500 text-xs mt-1">
                    Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Enter 10-digit mobile number without country code
                </p>
              </div>

              <button
                onClick={handleMobileSubmit}
                disabled={!mobile || !validateMobile(mobile)}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-base"
              >
                Continue
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center">
                <Package className="mx-auto text-amber-600 mb-2 sm:mb-4" size={36} />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
                  Quantity Required
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm">
                  Tell us how much you need for accurate pricing
                </p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Quantity Needed *
                </label>
                <div className="flex flex-col xs:flex-row space-y-2 xs:space-y-0 xs:space-x-2">
                  <input
                    type="number"
                    value={quantityValue}
                    onChange={(e) => setQuantityValue(e.target.value)}
                    className={`flex-1 px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs sm:text-base ${
                      !quantityValue.trim() && quantityValue !== '' ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter quantity"
                    min="1"
                  />
                  <select
                    value={quantityUnit}
                    onChange={(e) => setQuantityUnit(e.target.value)}
                    className="px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs sm:text-base"
                  >
                    <option value="kg">KG</option>
                    <option value="bags">Bags</option>
                    <option value="tons">Tons</option>
                    <option value="quintals">Quintals</option>
                  </select>
                </div>
                {!quantityValue.trim() && quantityValue !== '' && (
                  <p className="text-red-500 text-xs mt-1">
                    Please enter the quantity you need
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Your Location
                </label>
                <div className="flex flex-col xs:flex-row space-y-2 xs:space-y-0 xs:space-x-2">
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="flex-1 px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-xs sm:text-base"
                    placeholder="Enter your city, state, or address"
                  />
                  <button
                    type="button"
                    onClick={detectLocation}
                    disabled={isDetectingLocation}
                    className="w-full xs:w-auto px-4 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-xs sm:text-base"
                    title="Detect your current location"
                  >
                    {isDetectingLocation ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Navigation size={18} />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Click the location button to automatically detect your location
                </p>
              </div>

              {(productName || title) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 mb-1">Article/Product:</h4>
                  <p className="text-amber-700">{productName || title}</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setStep(1)}
                  disabled={isSubmitting}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 text-xs sm:text-base"
                >
                  Back
                </button>
                <button
                  onClick={handleFinalSubmit}
                  disabled={!quantityValue.trim() || isSubmitting}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-xs sm:text-base"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>Get Quote</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 text-center">
              <div>
                <CheckCircle className="mx-auto text-green-600 mb-4" size={64} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600">
                  Your inquiry has been submitted successfully. Our team will contact you shortly.
                </p>
              </div>

              {/* Contact Details */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 text-left">
                <h4 className="font-bold text-amber-800 mb-4 text-center">Contact Us Directly</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="text-amber-600 flex-shrink-0" size={20} />
                    <div>
                      <a href="tel:+919977724699" className="font-medium text-gray-900 hover:underline block">+91-9977724699</a>
                      <p className="text-sm text-gray-600">Primary Contact</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Phone className="text-amber-600 flex-shrink-0" size={20} />
                    <div>
                      <a href="tel:+919977765464" className="font-medium text-gray-900 hover:underline block">+91-9977765464</a>
                      <p className="text-sm text-gray-600">Alternative Contact</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Mail className="text-amber-600 flex-shrink-0" size={20} />
                    <div>
                      <a href="mailto:simranindustries143@gmail.com" className="font-medium text-gray-900 hover:underline block">simranindustries143@gmail.com</a>
                      <p className="text-sm text-gray-600">Email Us</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <MapPin className="text-amber-600 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">318/26, Bholaram Compound, Palda</p>
                      <p className="text-sm text-gray-600">Indore, MP - 452001</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Clock className="text-amber-600 flex-shrink-0" size={20} />
                    <div>
                      <p className="font-medium text-gray-900">Mon - Sat: 9:00 AM - 6:00 PM</p>
                      <p className="text-sm text-gray-600">Business Hours</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all duration-200"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogLeadPopup; 